import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {MentalHelper} from "../utils/MentalHelper";
import {Mental} from "../Mental";
import {BitmapText} from "pixi.js";
import {TimelineLite, TweenLite} from "gsap";
import {SoundManager} from "../sound/SoundManager";
import {MentalSoundConfig} from "../MentalSoundConfig";
import {MentalGameMode} from "../server/MentalGameMode";

export class DeadSymbolSticker extends PIXI.Container {
    public static _multUpdateSoundPlayed: boolean = false;
    public multiplierValue: number = 0;
    public _count: number = 0;
    public _isDim: boolean = false;
    private _icon: PIXI.Sprite;
    private readonly _symName: string;
    private _multiplier: PIXI.BitmapText;
    private _symCount: PIXI.Text;
    private _roundTextBg: PIXI.Container;
    private _fireAnim: TimelineSprite;
    private _lockImg: PIXI.Sprite;
    private _blastAnim: TimelineSprite;
    private _iconShine: PIXI.Sprite;

    constructor(symName: string, posOffSet: number) {
        super();
        this._symName = symName;
        this.position.set((144 * 3.5) + posOffSet * 50, (198 / 3.5));
        this.createSticker();
    }

    private static createPNGSeq(assetName: string): TimelineSprite {
        const anim: TimelineSprite = MentalHelper.createSprite(assetName);
        anim.hide();
        anim.blendMode = PIXI.BLEND_MODES.ADD;
        return anim;
    }

    private static createSymIcon(symbolName: String): PIXI.Sprite {
        const icon: PIXI.Sprite = new PIXI.Sprite(Mental.assetLoader.getTextures("m_symbol_icon/" + symbolName + "_icon")[0]);
        icon.anchor.set(0.5);
        icon.scale.set(0.65);

        return icon;
    }

    private static createLockImg(): PIXI.Sprite {
        const img: PIXI.Sprite = new PIXI.Sprite(Mental.assetLoader.getTextures("symLock")[0]);
        img.anchor.set(0.5);
        img.position.set(2, 6);
        img.scale.set(0.65);
        img.visible = false;

        return img;
    }

    private static createSymShine(): PIXI.Sprite {
        const iconShine: PIXI.Sprite = new PIXI.Sprite(Mental.assetLoader.getTextures("deadShine")[0]);
        iconShine.visible = false;
        iconShine.alpha = 0;
        iconShine.anchor.set(0.5);
        iconShine.scale.set(3);
        iconShine.blendMode = PIXI.BLEND_MODES.ADD;

        return iconShine;
    }

    private static createCircleBg(): PIXI.Container {
        const img: PIXI.Sprite = new PIXI.Sprite(Mental.assetLoader.getTextures("circleBg")[0]),
            imgCont: PIXI.Container = new PIXI.Container();

        img.anchor.set(0.5);
        img.scale.set(0.7);
        imgCont.addChild(img);
        return imgCont;
    }

    private static createSymCount(): PIXI.Text {
        return new PIXI.Text("", {
            fontFamily: "Titania",
            fontSize: 25,
            align: "center",
            fill: "#fff001",
            maxWidth: 30
        });
    }

    private static createMultiplier(): BitmapText {
        return new BitmapText("x0", {
            fontName: "MultiplierWinCountUp",
            fontSize: 8
        });
    }

    public updateMultiplierTo(toValue: number): void {
        this.multiplierValue = toValue;
        this._multiplier.text = "x" + toValue.toString();
    }

    public updateSymCount(toValue: number): void {
        this._count = toValue;
        this._symCount.text = toValue.toString();
    }

    public dimDown(): void {
        this.multiplierValue = 0;
        this._isDim = true;
        this.visible = false;
        this.hideCount();
        this.hideMult();
        this.hideFire();
        this.hideBlast();
        this.hideLocks();
    }

    public unDim(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        this._isDim = false;
        this.visible = true;
        tl.add(this.showBlast(), 0);
        Mental.deadFeatureStickyMultiplier.alignStickers();
        return tl;
    }

    public repositionSubChild(isLandscape: boolean): void {
        this._multiplier.scale.set(0.8);
        if (isLandscape) {
            this._symCount.position.set(25, -17);
            this._multiplier.position.set(55, 15);
            this._roundTextBg.position.set(25, -15);
            this._blastAnim.position.set(25, 20);
        } else {
            this._symCount.position.set(-1, -27);
            this._multiplier.position.set((this._multiplier.width / 2), 15);
            this._roundTextBg.position.set(0, -25);
            this._blastAnim.position.set(0, 40);
        }
    }

    public onlyShowIcon(): void {
        this.dimDown();
        this.visible = true;
        this._icon.visible = true;
        Mental.deadFeatureStickyMultiplier.alignStickers();
    }

    public showLocks(): void {
        if (Mental.model.data.mode === MentalGameMode.SUPER_FREESPIN || Mental.model.data.nextMode === MentalGameMode.SUPER_FREESPIN) {
            this._lockImg.visible = true;
        }
    }

    private createSticker(): void {
        this._fireAnim = DeadSymbolSticker.createPNGSeq("stickyFire");
        this._fireAnim.scale.set(1.35);

        this._lockImg = DeadSymbolSticker.createLockImg();

        this._icon = DeadSymbolSticker.createSymIcon(this._symName);
        this._iconShine = DeadSymbolSticker.createSymShine();
        this._roundTextBg = DeadSymbolSticker.createCircleBg();
        this._symCount = DeadSymbolSticker.createSymCount();
        this._multiplier = DeadSymbolSticker.createMultiplier();
        this._multiplier.anchor = 1;

        this._symCount.anchor.set(0.5);

        this._blastAnim = DeadSymbolSticker.createPNGSeq("multBlast");
        this._blastAnim.scale.set(1.5);

        this.addChild(this._fireAnim, this._icon, this._lockImg, this._iconShine, this._roundTextBg, this._symCount, this._multiplier, this._blastAnim);
    }

    private hideLocks(): void {
        this._lockImg.visible = false;
    }

    private hideCount(): void {
        this._roundTextBg.visible = false;
        this._symCount.visible = false;
    }

    private showCount(): void {
        this._roundTextBg.visible = true;
        this._symCount.visible = true;
        TweenLite.to([this._roundTextBg, this._symCount], 0.2, {
            alpha: 1,
            onStart: () => {
                TweenLite.to([this._roundTextBg.scale, this._symCount.scale], 0.1, {
                    x: 1.1, y: 1.1, onComplete: () => {
                        TweenLite.to([this._roundTextBg.scale, this._symCount.scale], 0.1, {
                            x: 1, y: 1
                        });
                    }
                });
            }
        })
    }

    private hideMult(): void {
        this._multiplier.visible = false;
    }

    private showMult(): void {
        this._multiplier.visible = true;
        TweenLite.to(this._multiplier, 0.1, {alpha: 1});
    }

    private hideFire(): void {
        this._fireAnim.stopLoop();
        this._fireAnim.hide();
    }

    private showFire(): void {
        TweenLite.to(this._fireAnim, 0.2, {alpha: 1});
        this._fireAnim.show();
        this._fireAnim.playLoop();
    }

    private hideBlast(): void {
        this._blastAnim.hide();
    }

    private showBlast(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();

        if (!(DeadSymbolSticker._multUpdateSoundPlayed)) {
            DeadSymbolSticker._multUpdateSoundPlayed = true;
            SoundManager.playSoundEffect(MentalSoundConfig.DEAD_COLLECT_UPDATE);
        }

        tl.add(
            this._blastAnim.getAnimationAutoShowHide(true, true).add(() => {
                this.showLocks();
                this.showMult();
                this.playShine();
            }, "-=0.05").add(() => {
                this.showFire();
            })
        );
        tl.add(() => {
            this.showCount();
        });

        return tl;
    }

    private playShine(): void {
        let fadeTime: number = 0.2;
        this._iconShine.visible = true;
        TweenLite.to(this._iconShine, fadeTime, {
            alpha: 1,
            onComplete: () => {
                TweenLite.to(this._iconShine, fadeTime, {
                    alpha: 0,
                    onComplete: () => {
                        this._iconShine.visible = false;
                    }
                });
            }
        });
    }
}